import { all, put, takeLatest, select } from "redux-saga/effects"

import { languages } from "locales/languages"
import { request } from "lib/request"
import { ResponsePayload } from "types/payload"
import { Action, GWatcher } from "types/redux"
import { CardInformation } from "types/stripe"

import actions from "./actions"
import {
  CARDS_REQUEST,
  DELETE_CARD_REQUEST,
  GET_TOKEN_REQUEST,
  SETUPINTENT_ATTACH_REQUEST,
  SETUPINTENT_CREATE_REQUEST,
} from "./constants"
import { PRICE_SUCCESS } from "containers/Payment/services/constants"
import { openNotification } from "components/Message/Message"
import { getCurrentPatientExemptionAnswer, getCurrentSelectedPatientId, getDoctorAvailableAnswer } from "containers/Survey/services/selector"
import { DoctorType, TreatingDoctorStatus } from "types/entity"

async function getpaymentApiKey(): Promise<{ payment_api_key: string }> {
  return await request("/cards/payment_api_key", {
    method: "GET",
  })
}

async function getCardsApi(): Promise<CardInformation[]> {
  return await request('/cards/methods/current', { method: "GET" })
}

async function deleteCardApi(payload: {
  payment_method_id: string
}): Promise<ResponsePayload> {
  return await request('/cards/methods/current', {
    method: "DELETE",
    payload,
  })
}

async function setupIntentAttachApi(payload: {
  setupintent_id: string
}) { //: Promise<ResponsePayload>
  return await request("/cards/setupintent/attach", {
    method: "POST",
    payload,
  })
}

async function setupPaymentIntentApi(payload) {
  return await request("/cards/paymentintent/create", { // /cards/setupintent/create
    method: "POST",
    payload: {
      final: true, doctor: false, relative_id: payload.relative_id, category_id: payload.category_id,
      treating_doctor_status: payload.treating_doctor_status,
    }
  })
}

async function setupIntentCreateApi() {
  // : Promise<{
  //   setupintent: { client_secret: string }
  // }>

  return await request("/cards/setupintent/create", {
    method: "POST",
  })
}
/*
 * Create SetupIntent | receive id
 */

function* paymentFlow(): any {
  try {
    const category_id = DoctorType.GENERALIST;
    const relative_id = yield select(getCurrentSelectedPatientId);
    const treating_doctor_status: TreatingDoctorStatus | undefined = yield select(getDoctorAvailableAnswer);

    const is_user_exempted = yield select(getCurrentPatientExemptionAnswer);
    // exempted patient don't have to pay avoid creating a line in stripe
    if(is_user_exempted) {
      return
    }

    const response = yield setupPaymentIntentApi({ relative_id: relative_id === 0 ? null : relative_id, category_id, treating_doctor_status })
    yield all([put(actions.paymentIntentSuccess(response)), put({ type: PRICE_SUCCESS, payload: response.price })])
  }
  catch (error) {
    console.error(error, {
      route: "/cards/paymentintent/create"
    })
    yield all([put(actions.createError(typeof error === "string" ? error : "Erreur")),
    ])
    openNotification({type: 'error', text: typeof error === "string" ? error : error?.message || "Erreur" });
  }
}

function* createFlow(): any { //: GFlow<Action<Message | string | any>>
  try {
    const {
      setupintent: { client_secret: clientSecret },
    } = yield setupIntentCreateApi()

    yield put(actions.createSuccess(clientSecret))

  } catch (error) {
    console.error(error, {
      route: "/cards/setupintent/create"
    })
    yield all([put(actions.createError(typeof error === "string" ? error : "Erreur")),
    ])
    openNotification({type: 'error', text: typeof error === "string" ? error : error?.message || "Erreur" });
  }
}

function* stripeTokenFlow() {
  try {
    const { payment_api_key }: { payment_api_key: string } =
      yield getpaymentApiKey()
    yield put(actions.getTokenSucess(payment_api_key))
  } catch (error) {
    console.error(error, {
      route: '/cards/payment_api_key'
    })
    yield all([put(
      actions.getTokenError(
        typeof error === "string" ? error : languages.genericAPIError
      )
    ),
    ])
    openNotification({type: 'error', text: error});
  }
}

function* attachFlow({
  payload,
}: Action<{ setupintent_id: string }>) { // : GFlow<Message>
  yield put(actions.hasStripeError(false))
  try {
    yield setupIntentAttachApi(payload)
    yield all([
      put(actions.attachSuccess(languages.createCardSuccess)),
      put(actions.cardsRequest()),
      put(actions.resetCard()),
    ])
  } catch (error) {
    console.error(error, {
      route: "/cards/setupintent/attach"
    })
    yield all([
      put(
        actions.attachError(
          typeof error === "string" ? error : languages.createCardError
        )
      ),
      put(actions.hasStripeError(true)),
    ])
    openNotification({type: 'error', text: error});
  }
}

function* cardsFlow() { // : GFlow<Action<Message>> 
  try {
    const { cards } = yield getCardsApi()
    yield put(actions.cardsSuccess(cards as CardInformation[]))
  } catch (error) {
    console.error(error, {
      route: '/cards/methods/current'
    })
    yield put(
      actions.cardsError(
        typeof error === "string" ? error : languages.genericAPIError
      )
    )
  }
}

function* deleteCardFlow({
  payload,
}: Action<{
  payment_method_id: string
}>): any { // : GFlow<Action<Message>> 
  try {
    const response = yield deleteCardApi(payload)
    if (response)
      yield all([
        put(actions.deleteCardSuccess("Votre carte a été supprimée")),
        put(actions.cardsRequest()),
      ])
      openNotification({type:'success', text: "Votre carte a été supprimée"});
  } catch (error) {
    console.error(error, {
      route: '/cards/methods/current'
    })
    yield put(
      actions.deleteCardError(
        typeof error === "string" ? error : languages.genericAPIError
      )
    )
  }
}
export default function* cardsSaga(): GWatcher {
  yield takeLatest(CARDS_REQUEST, cardsFlow)
  yield takeLatest(SETUPINTENT_ATTACH_REQUEST, attachFlow)
  yield takeLatest(SETUPINTENT_CREATE_REQUEST, createFlow)
  yield takeLatest("SETUP_PAYMENT_INTENT_REQUEST", paymentFlow)
  yield takeLatest(DELETE_CARD_REQUEST, deleteCardFlow)
  yield takeLatest(GET_TOKEN_REQUEST, stripeTokenFlow)
}
